import { GTM_LINKED_TAGS } from '@/lib/analytics/gtm';

const LOCALES = ['bg', 'en', 'de'] as const;

const QUERY_ALLOWLIST = ['service', 'moveType', 'sqm', 'frequency', 'effort', 'level'] as const;

const SERVICE_HUBS = new Set(['residential', 'commercial', 'outdoor', 'specialized']);

/** Slug → registry category and subgroup. Kept small so the layout tracker does not import the service registry. */
export const SERVICE_PAGE_META: Record<string, { category: string; subgroup: string }> = {
  'regular-home-cleaning': { category: 'Residential', subgroup: 'maintenance' },
  'move-in-out-cleaning': { category: 'Residential', subgroup: 'deep' },
  'seasonal-deep-cleaning': { category: 'Residential', subgroup: 'deep' },
  'window-cleaning': { category: 'Residential', subgroup: 'surfaces' },
  'textile-cleaning': { category: 'Residential', subgroup: 'surfaces' },
  'office-cleaning': { category: 'Commercial', subgroup: 'everyday' },
  'retail-cleaning': { category: 'Commercial', subgroup: 'everyday' },
  'restaurant-cafe-cleaning': { category: 'Commercial', subgroup: 'everyday' },
  'fitness-gym-cleaning': { category: 'Commercial', subgroup: 'everyday' },
  'salon-beauty-cleaning': { category: 'Commercial', subgroup: 'everyday' },
  'medical-practice-cleaning': { category: 'Commercial', subgroup: 'regulated' },
  'kindergarten-cleaning': { category: 'Commercial', subgroup: 'regulated' },
  'care-facility-cleaning': { category: 'Commercial', subgroup: 'regulated' },
  'holiday-apartment-cleaning': { category: 'Commercial', subgroup: 'turnover-events' },
  'trade-fair-cleaning': { category: 'Commercial', subgroup: 'turnover-events' },
  'stairwell-cleaning': { category: 'Outdoor', subgroup: 'common-areas' },
  'underground-garage-cleaning': { category: 'Outdoor', subgroup: 'common-areas' },
  'facade-cleaning': { category: 'Outdoor', subgroup: 'envelope' },
  'graffiti-removal': { category: 'Outdoor', subgroup: 'envelope' },
  'paved-surface-cleaning': { category: 'Outdoor', subgroup: 'envelope' },
  'gutter-cleaning': { category: 'Outdoor', subgroup: 'envelope' },
  gardening: { category: 'Outdoor', subgroup: 'grounds' },
  'pool-cleaning': { category: 'Outdoor', subgroup: 'grounds' },
  'post-construction-cleaning': { category: 'Specialized', subgroup: 'after-works' },
  'hoarding-cleaning': { category: 'Specialized', subgroup: 'sensitive' },
  'crime-scene-cleaning': { category: 'Specialized', subgroup: 'sensitive' },
  'furniture-transport': { category: 'Specialized', subgroup: 'logistics' },
};

export const PAGE_TITLE_WAIT_MS = 300;

export type PageContentGroup =
  | 'home'
  | 'services'
  | 'service'
  | 'districts'
  | 'district'
  | 'blog'
  | 'blog_category'
  | 'blog_article'
  | 'pricing'
  | 'booking'
  | 'booking_confirmation'
  | 'contact'
  | 'about'
  | 'faq'
  | 'how_it_works'
  | 'reviews'
  | 'cleaning_options'
  | 'providers'
  | 'legal'
  | 'insurance'
  | 'other';

export interface PageTag {
  page_path: string;
  page_location: string;
  page_title: string;
  page_referrer: string;
  page_locale: string;
  content_group: PageContentGroup;
  page_type: string;
  service_slug: string;
  service_category: string;
  service_subgroup: string;
  district_slug: string;
  blog_slug: string;
  booking_service: string;
  booking_frequency: string;
  booking_effort: string;
  booking_sqm: string;
}

const PRIVATE_SECTIONS = new Set(['admin', 'auth', 'dashboard', 'logout', 'provider']);

function splitPath(pathname: string): string[] {
  return pathname.split('/').filter(Boolean);
}

export function isPageTitleReady(
  initialTitle: string,
  currentTitle: string,
  elapsedMs: number,
): boolean {
  return currentTitle !== initialTitle || elapsedMs >= PAGE_TITLE_WAIT_MS;
}

export function classifyPublicPage(
  pathname: string,
): Omit<
  PageTag,
  | 'page_location'
  | 'page_title'
  | 'page_referrer'
  | 'booking_service'
  | 'booking_frequency'
  | 'booking_effort'
  | 'booking_sqm'
> | null {
  const parts = splitPath(pathname);
  const hasLocale = LOCALES.includes(parts[0] as (typeof LOCALES)[number]);
  const locale = hasLocale ? parts[0] : '';
  const rest = hasLocale ? parts.slice(1) : parts;
  const section = rest[0] ?? '';

  if (PRIVATE_SECTIONS.has(section) || rest.includes('dashboard')) {
    return null;
  }
  if (section.endsWith('-variants') || section.includes('typography')) {
    return null;
  }

  const page_path = `/${parts.join('/')}` || '/';
  const base = {
    page_path,
    page_locale: locale,
    service_slug: '',
    service_category: '',
    service_subgroup: '',
    district_slug: '',
    blog_slug: '',
  };

  if (rest.length === 0) {
    return { ...base, content_group: 'home', page_type: 'home' };
  }

  if (section === 'services') {
    if (!rest[1]) {
      return { ...base, content_group: 'services', page_type: 'services_index' };
    }
    if (SERVICE_HUBS.has(rest[1])) {
      return { ...base, content_group: 'service', page_type: 'service_hub', service_slug: rest[1] };
    }
    const meta = SERVICE_PAGE_META[rest[1]];
    return {
      ...base,
      content_group: 'service',
      page_type: 'service_detail',
      service_slug: rest[1],
      service_category: meta?.category ?? '',
      service_subgroup: meta?.subgroup ?? '',
    };
  }

  if (section === 'service-areas') {
    if (!rest[1]) {
      return { ...base, content_group: 'districts', page_type: 'districts_index' };
    }
    return {
      ...base,
      content_group: 'district',
      page_type: 'district_detail',
      district_slug: rest[1],
    };
  }

  if (section === 'blog') {
    if (!rest[1]) {
      return { ...base, content_group: 'blog', page_type: 'blog_index' };
    }
    if (rest[1] === 'category') {
      return {
        ...base,
        content_group: 'blog_category',
        page_type: 'blog_category',
        blog_slug: rest[2] ?? '',
      };
    }
    return {
      ...base,
      content_group: 'blog_article',
      page_type: 'blog_article',
      blog_slug: rest[1],
    };
  }

  if (section === 'book') {
    return { ...base, content_group: 'booking', page_type: 'booking' };
  }

  if (section === 'booking' && rest[1] === 'confirmation') {
    return {
      ...base,
      content_group: 'booking_confirmation',
      page_type: 'booking_confirmation',
    };
  }

  if (section === 'booking') {
    return null;
  }

  if (section === 'legal') {
    return {
      ...base,
      content_group: 'legal',
      page_type: rest[1] ? `legal_${rest[1].replace(/-/g, '_')}` : 'legal',
    };
  }

  const staticGroups: Record<string, PageContentGroup> = {
    pricing: 'pricing',
    contact: 'contact',
    about: 'about',
    faq: 'faq',
    'how-it-works': 'how_it_works',
    reviews: 'reviews',
    'cleaning-options': 'cleaning_options',
    providers: 'providers',
    'insurance-safety': 'insurance',
  };

  const content_group = staticGroups[section];
  if (content_group) {
    return { ...base, content_group, page_type: content_group };
  }

  return { ...base, content_group: 'other', page_type: section || 'other' };
}

export function allowedSearch(searchParams: URLSearchParams): string {
  const next = new URLSearchParams();
  for (const key of QUERY_ALLOWLIST) {
    const value = searchParams.get(key);
    if (value) {
      next.set(key, value);
    }
  }
  const query = next.toString();
  return query ? `?${query}` : '';
}

function bookingFields(searchParams: URLSearchParams) {
  return {
    booking_service: searchParams.get('service') ?? '',
    booking_frequency: searchParams.get('frequency') ?? '',
    booking_effort: searchParams.get('effort') ?? '',
    booking_sqm: searchParams.get('sqm') ?? '',
  };
}

export function pushGtmPageView(input: {
  pathname: string;
  searchParams: URLSearchParams;
  title?: string;
  origin?: string;
  referrer?: string;
}): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const classified = classifyPublicPage(input.pathname);
  if (!classified) {
    return null;
  }

  const query = allowedSearch(input.searchParams);
  const origin = input.origin ?? window.location.origin;
  const page: PageTag = {
    ...classified,
    ...bookingFields(input.searchParams),
    page_location: `${origin}${classified.page_path}${query}`,
    page_title: input.title ?? (typeof document === 'undefined' ? '' : document.title),
    page_referrer: input.referrer ?? '',
  };

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'virtual_page_view',
    ...page,
  });
  window.gtag?.('event', 'page_view', {
    page_location: page.page_location,
    page_path: page.page_path,
    page_title: page.page_title,
    page_referrer: page.page_referrer,
    send_to: GTM_LINKED_TAGS.ga4,
  });
  return page.page_location;
}
